import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LoadingIndicator } from '../../Tools/LoadingIndicator';
import { useWorkspace } from '../../../context/WorkspaceContext';
import TikTokLoginButton from '../../Buttons/TikTok/TikTokLoginButton';
import useThemeColors from '../../../hooks/useThemeColors';

// Import reusable components
import ChartCard from '../ChartCard';
import PlatformMetricCard from '../PlatformMetricCard';
import { SkeletonMetricCard, SkeletonTable, SkeletonHashtagGrid } from '../AnalyticsSkeletons';

// Import React Icons
import { 
  FiUsers, 
  FiEye, 
  FiHeart, 
  FiBarChart2, 
  FiPlay,
  FiAlertCircle,
  FiRefreshCw,
  FiVideo,
  FiTarget,
  FiHash,
  FiShare2,
  FiMessageCircle
} from 'react-icons/fi';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const TikTokAnalytics = () => {
  const { activeWorkspace, idToken } = useWorkspace();
  const navigate = useNavigate();
  const { isDark } = useThemeColors();

  // TikTok Analytics State
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [selectedTiktokAccount, setSelectedTiktokAccount] = useState(null);
  const [tiktokAnalytics, setTiktokAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  // API Functions
  const fetchTiktokAccounts = useCallback(async () => {
    if (!activeWorkspace || !idToken) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/tiktok/accounts`,
        {
          params: { workspaceId: activeWorkspace.id },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setTiktokAccounts(response.data.accounts || []);
      
      // Auto-select first account if available
      if (response.data.accounts && response.data.accounts.length > 0) {
        setSelectedTiktokAccount(response.data.accounts[0].platform_id);
      }
    } catch (error) {
      console.error('Error fetching TikTok accounts:', error);
      setAnalyticsError('Failed to load TikTok accounts. Please connect a TikTok account first.');
    }
  }, [activeWorkspace, idToken]);

  const fetchTiktokAnalytics = useCallback(async (platformId, days = 30) => {
    if (!activeWorkspace || !idToken || !platformId) return;
    
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/tiktok/analytics`,
        {
          params: { 
            workspaceId: activeWorkspace.id, 
            platform_id: platformId,
            days: days
          },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setTiktokAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching TikTok analytics:', error);
      if (error.response?.status === 401) {
        setAnalyticsError('TikTok token expired. Please reconnect your TikTok account.');
      } else if (error.response?.status === 429) {
        setAnalyticsError('TikTok API rate limit exceeded. Please try again later.');
      } else {
        setAnalyticsError('Failed to load TikTok analytics. Please try again.');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeWorkspace, idToken]);

  // Fetch TikTok accounts when component mounts
  useEffect(() => {
    if (activeWorkspace && idToken) {
      fetchTiktokAccounts();
    }
  }, [fetchTiktokAccounts, activeWorkspace, idToken]);

  // Fetch TikTok analytics when account or days selection changes
  useEffect(() => {
    if (selectedTiktokAccount) {
      fetchTiktokAnalytics(selectedTiktokAccount, selectedDays);
    }
  }, [selectedTiktokAccount, selectedDays, fetchTiktokAnalytics]);

  // Filter posts based on selected time period
  const getFilteredPosts = () => {
    if (!tiktokAnalytics?.posts) return [];
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (selectedDays * 24 * 60 * 60 * 1000));
    
    return tiktokAnalytics.posts.filter(post => {
      const postDate = new Date(post.timestamp);
      return postDate >= cutoffDate;
    });
  };

  const filteredPosts = getFilteredPosts();

  return (
    <>
      {/* TikTok Account Selection */}
      {tiktokAccounts.length > 1 && (
        <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4 --theme-colors">Select TikTok Account</h3>
          <div className="flex flex-wrap gap-3">
            {tiktokAccounts.map((account) => (
              <button
                key={account.platform_id}
                onClick={() => setSelectedTiktokAccount(account.platform_id)}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg border transition-colors ${
                  selectedTiktokAccount === account.platform_id
                    ? isDark
                      ? 'border-pink-400 bg-pink-900/30 text-pink-300'
                      : 'border-pink-500 bg-pink-50 text-pink-700'
                    : isDark
                      ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {account.logo && (
                  <img 
                    src={account.logo} 
                    alt={account.username}
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                  />
                )}
                <span className="font-medium text-sm sm:text-base">@{account.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {analyticsError && (
        <div className={`rounded-lg p-4 sm:p-6 border ${
          isDark
            ? 'bg-red-900/30 border-red-700'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <FiAlertCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <p className={isDark ? 'text-red-300' : 'text-red-800'}>{analyticsError}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <button
              onClick={() => fetchTiktokAccounts()}
              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'text-red-400 hover:text-red-300 border-red-700 hover:bg-red-900/20'
                  : 'text-red-600 hover:text-red-700 border-red-300 hover:bg-red-100'
              }`}
            >
              <FiRefreshCw className="w-4 h-4" />
              Retry
            </button>
            {analyticsError.includes('connect') && (
              <div className="w-full sm:w-auto sm:max-w-xs">
                <TikTokLoginButton />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Content */}
      {(selectedTiktokAccount || analyticsLoading) && !analyticsError && (
        <>
          {/* Account Display & Period Selection */}
          {!analyticsLoading && tiktokAnalytics && (
            <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Account Info - Left Side */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark
                      ? 'bg-gradient-to-r from-pink-600 to-red-600'
                      : 'bg-gradient-to-r from-pink-400 to-red-400'
                  }`}>
                    <FiVideo className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold --theme-colors">
                      {tiktokAccounts.find(acc => acc.platform_id === selectedTiktokAccount)?.username || 'TikTok Account'}
                    </h3>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Analytics for @{tiktokAccounts.find(acc => acc.platform_id === selectedTiktokAccount)?.username || 'account'}
                    </p>
                  </div>
                </div>
                
                {/* Period Selection - Right Side */}
                <div className="flex flex-wrap gap-2">
                  {[7, 30, 60, 90].map((days) => (
                    <button
                      key={days}
                      onClick={() => setSelectedDays(days)}
                      className={`px-3 sm:px-4 py-2 rounded-lg border transition-colors text-xs sm:text-sm ${
                        selectedDays === days
                          ? isDark
                            ? 'border-pink-400 bg-pink-900/30 text-pink-300'
                            : 'border-pink-500 bg-pink-50 text-pink-700'
                          : isDark
                            ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Last {days} days
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* All Metrics in Single Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {analyticsLoading ? (
              <>
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
              </>
            ) : tiktokAnalytics ? (
              <>
                <PlatformMetricCard
                  title="Followers"
                  value={tiktokAnalytics.overview.total_followers?.toLocaleString() || '0'}
                  icon={FiUsers}
                  platformColor="pink"
                  subtitle="Total"
                />
                <PlatformMetricCard
                  title="Avg Views"
                  value={tiktokAnalytics.overview.avg_views?.toLocaleString() || '0'}
                  icon={FiEye}
                  platformColor="pink"
                  subtitle="Per video"
                />
                <PlatformMetricCard
                  title="Total Videos"
                  value={tiktokAnalytics.overview.total_videos?.toLocaleString() || '0'}
                  icon={FiVideo}
                  platformColor="pink"
                  subtitle="Published"
                />
                <PlatformMetricCard
                  title="Engagement Rate"
                  value={tiktokAnalytics.overview.engagement_rate ? `${tiktokAnalytics.overview.engagement_rate}%` : 'N/A'}
                  icon={FiHeart}
                  platformColor="pink"
                  subtitle="Overall"
                />
              </>
            ) : null}
          </div>

          {/* Recent Posts Table */}
          {(analyticsLoading || tiktokAnalytics) && (
            <ChartCard
              title="Recent Videos Performance"
              description={analyticsLoading ? "Loading videos..." : `Showing ${filteredPosts?.length || 0} videos from the last ${selectedDays} days`}
              icon={FiBarChart2}
            >
              {analyticsLoading ? (
                <SkeletonTable />
              ) : filteredPosts && filteredPosts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Video</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</th>
                      <th className={`text-right py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Views</th>
                      <th className={`text-right py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Likes</th>
                      <th className={`text-right py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Comments</th>
                      <th className={`text-right py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Shares</th>
                      <th className={`text-right py-3 px-4 font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Engagement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPosts.map((post, index) => (
                      <tr key={post.id} className={`border-b ${isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded flex items-center justify-center ${
                              isDark ? 'bg-pink-900/40' : 'bg-pink-100'
                            }`}>
                              <FiVideo className={`w-4 h-4 ${isDark ? 'text-pink-400' : 'text-pink-600'}`} />
                            </div>
                            <div className="max-w-xs">
                              <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {post.description || post.title || 'No description'}
                              </p>
                              <div className="flex gap-1 mt-1">
                                {post.hashtags?.slice(0, 3).map((hashtag, i) => (
                                  <span key={i} className={`text-xs px-1 rounded ${
                                    isDark 
                                      ? 'text-pink-300 bg-pink-900/40' 
                                      : 'text-pink-600 bg-pink-100'
                                  }`}>
                                    {hashtag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {post.timestamp ? new Date(post.timestamp).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className={`py-3 px-4 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {post.views?.toLocaleString() || '0'}
                        </td>
                        <td className={`py-3 px-4 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {post.likes?.toLocaleString() || '0'}
                        </td>
                        <td className={`py-3 px-4 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {post.comments?.toLocaleString() || '0'}
                        </td>
                        <td className={`py-3 px-4 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {post.shares?.toLocaleString() || '0'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`text-sm font-medium ${
                            post.engagement_rate && parseFloat(post.engagement_rate) > 3 
                              ? isDark ? 'text-green-400' : 'text-green-600'
                              : isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {post.engagement_rate ? `${post.engagement_rate}%` : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              ) : (
                <div className="text-center py-12">
                  <FiVideo className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <h3 className={`text-base sm:text-lg font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No videos in the last {selectedDays} days</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Try selecting a longer time period or publish some content to see your performance metrics.
                  </p>
                </div>
              )}
            </ChartCard>
          )}

          {/* Hashtag Analytics */}
          {(analyticsLoading || (tiktokAnalytics?.hashtags && tiktokAnalytics.hashtags.length > 0)) && (
            <ChartCard
              title="Hashtag Analytics"
              description={analyticsLoading ? "Loading hashtag data..." : "Most used hashtags in your recent videos"}
              icon={FiHash}
            >
              {analyticsLoading ? (
                <SkeletonHashtagGrid />
              ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {tiktokAnalytics.hashtags.slice(0, 12).map((hashtag, index) => (
                  <div key={index} className={`rounded-lg p-4 text-center ${
                    isDark ? 'bg-pink-900/30' : 'bg-pink-50'
                  }`}>
                    <div className={`text-base sm:text-lg font-bold ${isDark ? 'text-pink-300' : 'text-pink-600'}`}>
                      {hashtag.hashtag}
                    </div>
                    <div className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-pink-400' : 'text-pink-500'}`}>
                      Used {hashtag.usage_count} times
                    </div>
                  </div>
                ))}
              </div>
              )}
            </ChartCard>
          )}

        </>
      )}

      {/* No Data State */}
      {!tiktokAnalytics && !analyticsLoading && !analyticsError && tiktokAccounts.length === 0 && (
        <div className="text-center py-12">
          <FiTarget className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`text-base sm:text-lg font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>No TikTok Account Connected</h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Connect your TikTok account to view analytics.
          </p>
          <button
            onClick={() => navigate('/socials')}
            className={`px-6 py-2 rounded-lg transition-colors ${
              isDark 
                ? 'bg-pink-600 hover:bg-pink-700 text-white' 
                : 'bg-pink-600 hover:bg-pink-700 text-white'
            }`}
          >
            Connect TikTok Account
          </button>
        </div>
      )}
    </>
  );
};

export default TikTokAnalytics;